import React, { useMemo, useState, useEffect } from 'react';
import { useModal } from '../contexts/ModalContext';
import EmptyState from '../components/EmptyState';
import { useCertificatesData } from '../hooks/useData';
import { certificatesApi } from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../hooks/useData';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentCheckIcon,
  BuildingOfficeIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Search as SearchIcon } from 'lucide-react';
import { CertificateActionsMenu } from '../components/Certificates/CertificateActionsMenu';

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function Certificates() {
  const { openModal } = useModal();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  
  // Use React Query for cached data
  const { data: allCertificates = [], isLoading: loading, error } = useCertificatesData();
  const [filteredCertificates, setFilteredCertificates] = useState<any[]>([]);

  const [viewLoadingId, setViewLoadingId] = useState<string>('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCertificateId, setDrawerCertificateId] = useState<string>('');
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [drawerPdfLoading, setDrawerPdfLoading] = useState(false);
  const [drawerPdfUrl, setDrawerPdfUrl] = useState<string>('');
  const [drawerCertificate, setDrawerCertificate] = useState<any>(null);
  const [drawerFilledDraft, setDrawerFilledDraft] = useState<Record<string, any>>({});
  const [drawerNewFieldKey, setDrawerNewFieldKey] = useState('');
  const [drawerNewFieldValue, setDrawerNewFieldValue] = useState('');
  
  // Pagination and filters state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const itemsPerPage = 10;

  // Client-side filtering - no loading, instant results
  useEffect(() => {
    let filtered = [...allCertificates];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(cert => 
        cert.certificateNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(cert.type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cert.client?.address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(cert => cert.status === statusFilter);
    }

    // Apply type filter with normalization
    if (typeFilter) {
      filtered = filtered.filter(cert => {
        // Normalize both the filter value and certificate type for comparison
        const normalizedCertType = cert.type?.toLowerCase().replace(/[_\s]/g, '');
        const normalizedFilterType = typeFilter.toLowerCase().replace(/[_\s]/g, '');
        return normalizedCertType === normalizedFilterType;
      });
    }

    setFilteredCertificates(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [allCertificates, searchTerm, statusFilter, typeFilter]);

  useEffect(() => {
    const onDocPointerDown = () => {};
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerCertificateId('');
    setDrawerCertificate(null);
    setDrawerFilledDraft({});
    setDrawerPdfUrl('');
    setDrawerNewFieldKey('');
    setDrawerNewFieldValue('');
  };

  const openDrawer = async (certificateId: string) => {
    const id = String(certificateId || '').trim();
    if (!id) return;

    setDrawerOpen(true);
    setDrawerCertificateId(id);

    try {
      setDrawerLoading(true);
      const res = await certificatesApi.getById(id);
      const payload = res.data;
      const cert = payload?.data || payload;
      setDrawerCertificate(cert);
      setDrawerFilledDraft(cert?.filledData && typeof cert.filledData === 'object' ? cert.filledData : {});
    } catch (e: any) {
      showToast(e?.response?.data?.message || e?.message || 'Failed to load certificate', 'error');
    } finally {
      setDrawerLoading(false);
    }

    try {
      setDrawerPdfLoading(true);
      const res = await certificatesApi.generatePdf(id);
      const payload = res.data;
      const result = payload?.data || payload;
      const url = result?.url;
      setDrawerPdfUrl(url || '');
    } catch {
      setDrawerPdfUrl('');
    } finally {
      setDrawerPdfLoading(false);
    }
  };

  const drawerFilledEntries = useMemo(() => {
    const entries = Object.entries(drawerFilledDraft || {});
    entries.sort((a, b) => String(a[0]).localeCompare(String(b[0])));
    return entries;
  }, [drawerFilledDraft]);

  const setDrawerFilledValue = (key: string, value: any) => {
    setDrawerFilledDraft((prev) => ({
      ...(prev || {}),
      [key]: value,
    }));
  };

  const removeDrawerFilledKey = (key: string) => {
    setDrawerFilledDraft((prev) => {
      const next = { ...(prev || {}) };
      delete next[key];
      return next;
    });
  };

  const handleDrawerSave = async () => {
    const id = String(drawerCertificateId || '').trim();
    if (!id) return;

    try {
      setDrawerSaving(true);
      await certificatesApi.update(id, {
        certificateNumber: String(drawerCertificate?.certificateNumber || '').trim() || undefined,
        status: String(drawerCertificate?.status || '').trim() || undefined,
        issueDate: drawerCertificate?.issueDate || undefined,
        expiryDate: drawerCertificate?.expiryDate || undefined,
        notes: drawerCertificate?.notes || undefined,
        filledData: drawerFilledDraft,
        jobId: drawerCertificate?.jobId || undefined,
        siteId: drawerCertificate?.siteId || undefined,
        templateId: drawerCertificate?.templateId || undefined,
        clientId: drawerCertificate?.clientId || undefined,
      });

      await queryClient.invalidateQueries({ queryKey: queryKeys.certificates });
      showToast('Certificate saved', 'success');

      setDrawerPdfLoading(true);
      const regenerated = await certificatesApi.generatePdf(id);
      const payload = regenerated.data;
      const result = payload?.data || payload;
      setDrawerPdfUrl(result?.url || '');
    } catch (e: any) {
      showToast(e?.response?.data?.message || e?.message || 'Failed to save certificate', 'error');
    } finally {
      setDrawerSaving(false);
      setDrawerPdfLoading(false);
    }
  };

  const handleViewCertificate = async (certificateId: string) => {
    const id = String(certificateId || '').trim();
    if (!id) return;

    try {
      setViewLoadingId(id);
      const res = await certificatesApi.generatePdf(id);
      const payload = res.data;
      const result = payload?.data || payload;
      const url = result?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        showToast('No PDF URL returned', 'error');
      }
    } catch (e: any) {
      showToast(e?.response?.data?.message || e?.message || 'Failed to open certificate', 'error');
    } finally {
      setViewLoadingId('');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Certificates</h1>
            <p className="page-subtitle">Loading...</p>
          </div>
        </div>
        <div className="table-container">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="loading-skeleton skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Certificates</h1>
            <p className="page-subtitle">Manage gas safety certificates, electrical certificates, and compliance documentation.</p>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-primary"
              onClick={() => openModal('createCertificate')}
            >
              Issue Certificate
            </button>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-red-900 mb-2">Error Loading Certificates</h3>
          <p className="text-red-700 mb-4">{error?.message || 'Failed to load certificates'}</p>
          <button 
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Calculate pagination for filtered results - ensure filteredCertificates is not empty
  const certificatesToShow = filteredCertificates.length > 0 ? filteredCertificates : allCertificates;
  const totalPages = Math.ceil(certificatesToShow.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCertificates = certificatesToShow.slice(startIndex, startIndex + itemsPerPage);

  if (allCertificates.length === 0 && !loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Certificates</h1>
        </div>
        <EmptyState
          icon={DocumentCheckIcon}
          title="No certificates yet"
          description="Issue your first certificate to start tracking compliance and safety documentation."
          actionLabel="Issue Certificate"
          onAction={() => openModal('createCertificate')}
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="mb-6 border-b border-[#E6E6E4] pb-3">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-[#191919] leading-tight">Certificates</h1>
              <p className="mt-1 text-xs font-normal text-[#515959]">
                Manage gas safety, electrical certificates, and compliance documents for every property.
              </p>
            </div>

            <div className="flex flex-1 items-center justify-start gap-3 md:justify-end md:gap-4">
              {/* Header Search */}
              <div className="relative w-full max-w-xs">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#686E6C]" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search certificates..."
                  className="h-9 w-full rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] pl-9 pr-3 text-sm text-[#383838] placeholder:text-[#686E6C] focus:outline-none focus:ring-2 focus:ring-[#CDCDCD]/80"
                />
              </div>

              {/* Primary Action */}
              <button
                type="button"
                onClick={() => openModal('createCertificate')}
                className="inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] px-3 py-2 text-sm font-medium text-[#383838] hover:bg-[#F3F2F0] transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Issue certificate
              </button>
            </div>
          </div>
        </div>

      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="filter-group">
          <select 
            className="filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Certificates</option>
            <option value="GAS_SAFETY">Gas Safety</option>
            <option value="ELECTRICAL">Electrical</option>
            <option value="BOILER_SERVICE">Boiler Service</option>
            <option value="LANDLORD_SAFETY">Landlord Safety</option>
          </select>
          
          <select 
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="EXPIRED">Expired</option>
            <option value="REVOKED">Revoked</option>
          </select>
          
          <select className="filter-select">
            <option>All Engineers</option>
          </select>
        </div>
        
        <div className="flex items-center gap-3 ml-auto">
          <button className="btn btn-secondary">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="table-container">
        <div className="table-wrapper">
          <table className="transactions-table">
            <thead className="table-header">
              <tr>
                <th className="col-checkbox">
                  <input type="checkbox" className="table-checkbox" />
                </th>
                <th className="col-description">Certificate Details</th>
                <th className="col-account">Customer & Property</th>
                <th className="col-amount">Engineer</th>
                <th className="col-date">Issue Date</th>
                <th className="col-payment">Expiry Date</th>
                <th className="col-actions">Status</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {paginatedCertificates.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8">
                    <div className="text-gray-500">
                      {filteredCertificates.length === 0 && (searchTerm || statusFilter || typeFilter) ? (
                        <>
                          <DocumentCheckIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="font-medium mb-1">No certificates found</p>
                          <p className="text-sm">Try adjusting your search or filters</p>
                        </>
                      ) : (
                        <>
                          <DocumentCheckIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p>No certificates on this page</p>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedCertificates.map((certificate: any) => (
                <tr
                  key={certificate.id}
                  className="table-row"
                  onClick={() => openDrawer(String(certificate.id))}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="col-checkbox">
                    <input type="checkbox" className="table-checkbox" onPointerDown={(e) => e.stopPropagation()} />
                  </td>
                  <td className="col-description">
                    <div className="transaction-description">
                      <div className="transaction-icon-wrapper">
                        <DocumentCheckIcon 
                          className="w-5 h-5"
                          style={{ color: '#6B7280' }}
                        />
                      </div>
                      <div className="transaction-details">
                        <h4>{certificate.certificateNumber}</h4>
                        <p>{certificate.type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="col-account">
                    <div className="flex flex-col">
                      <div className="flex items-center mb-1">
                        <UserIcon className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium">{certificate.client?.name || 'Unknown Client'}</span>
                      </div>
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600">{certificate.client?.address || 'No address'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="col-amount">
                    <span className="text-sm font-medium">{certificate.issuedBy?.name || '—'}</span>
                  </td>
                  <td className="col-date">
                    <span className="text-sm">{formatDate(certificate.issueDate)}</span>
                  </td>
                  <td className="col-payment">
                    <span className="text-sm">{formatDate(certificate.expiryDate)}</span>
                  </td>
                  <td className="col-actions">
                    <span className={`status-badge ${certificate.status.toLowerCase()}`}>
                      {certificate.status}
                    </span>
                  </td>
                  <td className="col-actions">
                    <CertificateActionsMenu
                      certificate={certificate}
                      onView={(id) => handleViewCertificate(id)}
                      viewLoading={viewLoadingId === certificate.id}
                    />
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="pagination">
          <div className="pagination-info">
            Page {currentPage} of {totalPages || 1}
          </div>
          <div className="pagination-controls">
            <button className="page-btn">
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <button className="page-btn active">1</button>
            <button className="page-btn">2</button>
            <button className="page-btn">3</button>
            <button className="page-btn">
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {drawerOpen ? (
        <>
          <div
            onClick={closeDrawer}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.2)',
              backdropFilter: 'blur(4px)',
              zIndex: 100,
            }}
          />

          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: 'min(56vw, 920px)',
              minWidth: '520px',
              background: '#FFFFFF',
              zIndex: 101,
              borderLeft: '1px solid #E6E6E4',
              overflowY: 'auto',
            }}
          >
            <div className="px-5 py-4 border-b border-[#E6E6E4] flex items-start justify-between gap-3">
              <div>
                <div className="text-xs text-[#686E6C]">Certificate</div>
                <div className="text-lg font-semibold text-[#191919]">
                  {drawerCertificate?.certificateNumber || '—'}
                </div>
                <div className="mt-1 text-xs text-[#686E6C]">{drawerCertificate?.type || '—'}</div>
              </div>

              <button
                type="button"
                onClick={closeDrawer}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#CDCDCD] bg-white text-[#383838] hover:bg-[#F3F2F0]"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 gap-5">
              {drawerLoading ? (
                <div className="text-sm text-[#686E6C]">Loading…</div>
              ) : (
                <>
                  <div className="rounded-xl border border-[#E6E6E4] bg-white p-4">
                    <div className="text-sm font-semibold text-[#181818]">Details</div>

                    <div className="mt-3 grid grid-cols-1 gap-3">
                      <div className="form-group">
                        <label className="form-label">Certificate number</label>
                        <input
                          className="form-input"
                          value={drawerCertificate?.certificateNumber || ''}
                          onChange={(e) =>
                            setDrawerCertificate((prev: any) => ({ ...(prev || {}), certificateNumber: e.target.value }))
                          }
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={drawerCertificate?.status || ''}
                          onChange={(e) =>
                            setDrawerCertificate((prev: any) => ({ ...(prev || {}), status: e.target.value }))
                          }
                        >
                          <option value="ACTIVE">ACTIVE</option>
                          <option value="EXPIRED">EXPIRED</option>
                          <option value="REVOKED">REVOKED</option>
                        </select>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Issue date</label>
                          <input
                            type="date"
                            className="form-input"
                            value={drawerCertificate?.issueDate ? String(drawerCertificate.issueDate).slice(0, 10) : ''}
                            onChange={(e) =>
                              setDrawerCertificate((prev: any) => ({ ...(prev || {}), issueDate: e.target.value }))
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Expiry date</label>
                          <input
                            type="date"
                            className="form-input"
                            value={drawerCertificate?.expiryDate ? String(drawerCertificate.expiryDate).slice(0, 10) : ''}
                            onChange={(e) =>
                              setDrawerCertificate((prev: any) => ({ ...(prev || {}), expiryDate: e.target.value }))
                            }
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Notes</label>
                        <textarea
                          className="form-textarea"
                          rows={3}
                          value={drawerCertificate?.notes || ''}
                          onChange={(e) => setDrawerCertificate((prev: any) => ({ ...(prev || {}), notes: e.target.value }))}
                        />
                      </div>

                      <div className="text-xs text-[#686E6C]">
                        Customer: <span className="text-[#181818]">{drawerCertificate?.client?.name || '—'}</span>
                      </div>
                      <div className="text-xs text-[#686E6C]">
                        Property: <span className="text-[#181818]">{drawerCertificate?.site?.address || drawerCertificate?.client?.address || '—'}</span>
                      </div>
                      <div className="text-xs text-[#686E6C]">
                        Engineer: <span className="text-[#181818]">{drawerCertificate?.issuedBy?.name || '—'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-[#E6E6E4] bg-white p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-[#181818]">Filled data</div>
                      <div className="text-xs text-[#686E6C]">{drawerFilledEntries.length} fields</div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-3">
                      {drawerFilledEntries.length === 0 ? (
                        <div className="text-sm text-[#686E6C]">No filled fields.</div>
                      ) : (
                        drawerFilledEntries.map(([k, v]) => {
                          const key = String(k);
                          const isBool = typeof v === 'boolean';
                          return (
                            <div key={key} className="rounded-lg border border-[#E6E6E4] bg-white p-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="text-xs font-medium text-[#181818] break-all">{key}</div>
                                <button
                                  type="button"
                                  className="text-xs text-[#686E6C] hover:text-[#181818]"
                                  onClick={() => removeDrawerFilledKey(key)}
                                >
                                  Remove
                                </button>
                              </div>

                              <div className="mt-2">
                                {isBool ? (
                                  <label className="flex items-center gap-2 text-sm">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(v)}
                                      onChange={(e) => setDrawerFilledValue(key, e.target.checked)}
                                    />
                                    <span className="text-[#686E6C]">Checked</span>
                                  </label>
                                ) : (
                                  <input
                                    className="form-input"
                                    value={v == null ? '' : String(v)}
                                    onChange={(e) => setDrawerFilledValue(key, e.target.value)}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}

                      <div className="rounded-lg border border-[#E6E6E4] bg-[#FAFAF9] p-3">
                        <div className="text-xs font-medium text-[#181818]">Add field</div>
                        <div className="mt-2 grid grid-cols-1 gap-2">
                          <input
                            className="form-input"
                            placeholder="pdfFieldName"
                            value={drawerNewFieldKey}
                            onChange={(e) => setDrawerNewFieldKey(e.target.value)}
                          />
                          <input
                            className="form-input"
                            placeholder="value"
                            value={drawerNewFieldValue}
                            onChange={(e) => setDrawerNewFieldValue(e.target.value)}
                          />
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg bg-orange-500 px-3 py-2 text-xs font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                            disabled={!drawerNewFieldKey.trim()}
                            onClick={() => {
                              const k = drawerNewFieldKey.trim();
                              if (!k) return;
                              setDrawerFilledDraft((prev) => ({ ...(prev || {}), [k]: drawerNewFieldValue }));
                              setDrawerNewFieldKey('');
                              setDrawerNewFieldValue('');
                            }}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-[#E6E6E4] bg-white p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-[#181818]">PDF preview</div>
                      <div className="text-xs text-[#686E6C]">{drawerPdfLoading ? 'Loading…' : ''}</div>
                    </div>

                    <div className="mt-3">
                      {drawerPdfUrl ? (
                        <iframe
                          title="certificate-pdf"
                          src={drawerPdfUrl}
                          style={{ width: '100%', height: 520, border: '1px solid #E6E6E4', borderRadius: 12 }}
                        />
                      ) : (
                        <div className="text-sm text-[#686E6C]">No preview available.</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-white px-3 py-2 text-sm font-medium text-[#383838] hover:bg-[#F3F2F0]"
                      onClick={closeDrawer}
                      disabled={drawerSaving}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                      onClick={handleDrawerSave}
                      disabled={drawerSaving}
                    >
                      {drawerSaving ? 'Saving…' : 'Save & regenerate'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
